(() => {
  const canonical = (value) => Array.isArray(value) ? value.map(canonical) : value && typeof value === 'object' ? Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])])) : value;
  const planId = (line) => line.selling_plan_allocation ? String(line.selling_plan_allocation.selling_plan?.id || '') : null;
  const lineIdentity = (line) => JSON.stringify(canonical([String(line.product_id), String(line.variant_id || line.id), line.properties || {}, planId(line), line.parent_relationship || null, line.parent_id || null, line.item_components || [], line.components || [], Boolean(line.remote), Boolean(line.gift_card)]));
  const supportedLine = (line) => Boolean(line && !(line.item_components || []).length && !(line.components || []).length && !line.has_components && !line.parent_relationship && !line.parent_id && !line.remote && (!line.selling_plan_allocation || planId(line)) && !Array.isArray(line.properties) && Object.values(line.properties || {}).every((value) => value === null || ['string', 'number', 'boolean'].includes(typeof value)));
  const quantities = (cart) => {
    if (!Array.isArray(cart?.items)) return null;
    const totals = new Map();
    for (const line of cart.items) {
      if (!Number.isSafeInteger(line.quantity) || line.quantity < 1) return null;
      const identity = lineIdentity(line);
      totals.set(identity, (totals.get(identity) || 0) + line.quantity);
    }
    return [...totals].sort(([a], [b]) => a.localeCompare(b));
  };
  const sameCart = (left, right) => quantities(left) !== null && quantities(right) !== null && JSON.stringify(quantities(left)) === JSON.stringify(quantities(right));
  const variantOptions = (variant) => variant.options || [variant.option1, variant.option2, variant.option3].filter((value) => value !== null && value !== undefined);
  const replacementPlan = (cart, product, input) => {
    if (!Array.isArray(cart?.items) || !Array.isArray(product?.variants)) return { error: 'load' };
    const line = cart.items.find((item) => item.key === input.key);
    if (!line || String(line.variant_id || line.id) !== input.currentId || String(line.product_id) !== input.productId || line.quantity !== input.quantity) return { error: 'stale' };
    if (!supportedLine(line) || String(product.id) !== input.productId || !Number.isSafeInteger(input.quantity) || input.quantity < 1 || input.currentId === input.targetId || !Number.isInteger(input.sizeIndex) || input.sizeIndex < 0) return { error: 'ineligible' };
    const originalIdentity = lineIdentity(line);
    if (cart.items.filter((item) => lineIdentity(item) === originalIdentity).length !== 1 || cart.items.some((item) => item.parent_relationship?.parent_key === line.key || String(item.parent_id || '') === input.currentId)) return { error: 'ineligible' };
    const current = product.variants.find((variant) => String(variant.id) === input.currentId);
    const target = product.variants.find((variant) => String(variant.id) === input.targetId);
    if (target?.available !== true) return { error: 'unavailable' };
    const currentOptions = current && variantOptions(current);
    const targetOptions = variantOptions(target);
    const sizeOption = product.options?.[input.sizeIndex];
    const optionName = String(typeof sizeOption === 'string' ? sizeOption : sizeOption?.name || '').trim().toLowerCase();
    if (!['size', 'sizes'].includes(optionName) || !currentOptions?.[input.sizeIndex] || !targetOptions[input.sizeIndex] || currentOptions.length !== targetOptions.length || currentOptions.some((value, index) => index !== input.sizeIndex && value !== targetOptions[index])) return { error: 'ineligible' };
    const sellingPlan = planId(line);
    if (sellingPlan && !target.selling_plan_allocations?.some((allocation) => String(allocation.selling_plan_id) === sellingPlan)) return { error: 'ineligible' };
    if (!sellingPlan && (product.requires_selling_plan || target.requires_selling_plan)) return { error: 'ineligible' };
    const targetLine = { ...line, key: '', id: target.id, variant_id: target.id };
    const targetIdentity = lineIdentity(targetLine);
    const targetExistingQuantity = cart.items.filter((item) => lineIdentity(item) === targetIdentity).reduce((total, item) => total + item.quantity, 0);
    const rule = target.quantity_rule || input.rule;
    const targetQuantity = targetExistingQuantity + input.quantity;
    if (!rule || !Number.isSafeInteger(rule.min) || rule.min < 1 || !Number.isSafeInteger(rule.increment) || rule.increment < 1 || targetQuantity < rule.min || (rule.max && targetQuantity > rule.max) || targetQuantity % rule.increment !== 0) return { error: 'ineligible' };
    const addItem = { id: target.id, quantity: input.quantity, properties: { ...(line.properties || {}) } };
    if (sellingPlan) addItem.selling_plan = sellingPlan;
    return { line, originalIdentity, targetIdentity, addItem, afterAdd: { items: [...cart.items, targetLine] }, afterReplace: { items: [...cart.items.filter((item) => item.key !== line.key), targetLine] } };
  };
  const removableOriginal = (cart, plan) => {
    if (!sameCart(cart, plan.afterAdd)) return null;
    const matches = cart.items.filter((line) => lineIdentity(line) === plan.originalIdentity);
    return matches.length === 1 && matches[0].quantity === plan.line.quantity && matches[0].key ? matches[0] : null;
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = { canonical, lineIdentity, supportedLine, quantities, sameCart, replacementPlan, removableOriginal };
  if (typeof window === 'undefined' || window.__fsLiveCartReady) return;
  window.__fsLiveCartReady = true;
  const storageKey = 'fs-live-cart-review';
  let replacing = false;
  let queued = false;
  let queueTimer;
  let uncertain = false;
  let reviewRequired = false;
  let messageKey = '';
  let activeEditor = null;
  let scheduled = false;
  const nativeBusy = () => Boolean(document.querySelector('.cart__items--disabled'));
  const busy = () => replacing || queued || nativeBusy();
  const checkoutLocked = () => busy() || uncertain || reviewRequired;
  const mutationLocked = () => busy() || uncertain;
  const readMarker = () => { try { return sessionStorage.getItem(storageKey); } catch (_) { return null; } };
  const markReview = () => { try { sessionStorage.setItem(storageKey, 'required'); return sessionStorage.getItem(storageKey) === 'required'; } catch (_) { return false; } };
  const clearReview = () => { try { sessionStorage.removeItem(storageKey); } catch (_) {} };
  const setText = (element, text) => { if (element && element.textContent !== text) element.textContent = text; };
  const lockControl = (control, lock) => {
    if (lock && !control.disabled) { control.dataset.fsLiveCartLocked = 'true'; control.disabled = true; }
    if (!lock && control.dataset.fsLiveCartLocked) { delete control.dataset.fsLiveCartLocked; control.disabled = false; }
  };
  const sync = () => {
    scheduled = false;
    if (nativeBusy() && queued) { queued = false; clearTimeout(queueTimer); }
    if ([...document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors')].some((element) => element.textContent.trim())) { uncertain = true; markReview(); }
    const pending = busy();
    document.documentElement.toggleAttribute('data-fs-live-cart-checkout-locked', checkoutLocked());
    document.querySelectorAll('[data-fs-live-cart-root]').forEach((root) => { if (root.getAttribute('aria-busy') !== String(pending)) root.setAttribute('aria-busy', String(pending)); });
    document.querySelectorAll('.cart__checkout-button, button[name="checkout"], input[name="checkout"]').forEach((control) => lockControl(control, checkoutLocked()));
    document.querySelectorAll('[data-fs-live-cart-root] quantity-input button, [data-fs-live-cart-root] .quantity__input, [data-fs-live-cart-root] cart-note textarea').forEach((control) => lockControl(control, mutationLocked()));
    document.querySelectorAll('[data-fs-live-cart-variant]').forEach((control) => lockControl(control, checkoutLocked()));
    document.querySelectorAll('.cart__dynamic-checkout-buttons').forEach((element) => { element.inert = checkoutLocked(); });
    document.querySelectorAll('fs-live-cart-editor').forEach((editor) => editor.sync?.());
    const key = pending ? 'busy' : uncertain ? (messageKey === 'stale' ? 'stale' : 'error') : reviewRequired ? 'review' : messageKey;
    document.querySelectorAll('[data-fs-live-cart-feedback]').forEach((panel) => {
      panel.hidden = !key;
      panel.dataset.state = key;
      setText(panel.querySelector('[data-fs-live-cart-status]'), panel.dataset[key] || '');
      const refreshedPage = Boolean(document.querySelector('cart-items[data-fs-live-cart-root]'));
      panel.querySelector('[data-fs-live-cart-reviewed]').hidden = !reviewRequired || uncertain || pending || !refreshedPage;
      panel.querySelector('[data-fs-live-cart-refresh]').hidden = pending || key === 'success';
      if (activeEditor?.isConnected) setText(activeEditor.querySelector('[data-fs-live-cart-editor-status]'), panel.dataset[key] || '');
    });
  };
  const schedule = () => { if (!scheduled) { scheduled = true; queueMicrotask(sync); } };
  const queueNative = () => {
    queued = true;
    messageKey = '';
    clearTimeout(queueTimer);
    queueTimer = setTimeout(() => { queued = false; sync(); }, 1000);
    sync();
  };
  async function requestJSON(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), options.method ? 20000 : 12000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      const data = await response.json();
      if (!response.ok || data.errors || data.status >= 400) throw new Error('Unconfirmed cart response');
      return data;
    } finally { clearTimeout(timer); }
  }
  const post = (url, body) => requestJSON(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
  const cartDestination = (url, result) => {
    const destination = new URL(url, location.origin);
    if (destination.origin !== location.origin) throw new Error('Invalid route');
    destination.searchParams.set('fs_cart_result', result);
    return destination.href;
  };
  class FsLiveCartEditor extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.select = this.querySelector('[data-fs-live-cart-variant]');
      this.button = this.querySelector('[data-fs-live-cart-apply]');
      this.addEventListener('change', (event) => { event.stopPropagation(); messageKey = ''; this.sync(); schedule(); });
      this.button?.addEventListener('click', () => this.apply());
      this.sync();
    }
    sync() { if (this.button) this.button.disabled = checkoutLocked() || !this.select?.value || this.select.value === this.dataset.variantId || Boolean(this.select.selectedOptions?.[0]?.disabled); }
    async apply() {
      if (checkoutLocked() || !this.select?.value || this.select.value === this.dataset.variantId || this.select.selectedOptions?.[0]?.disabled) return;
      const selected = this.select.selectedOptions[0];
      const input = { key: this.dataset.key, quantity: Number(this.dataset.quantity), productId: this.dataset.productId, currentId: this.dataset.variantId, targetId: this.select.value, sizeIndex: Number(this.dataset.sizeIndex), rule: { min: Number(selected.dataset.min || 1), max: Number(selected.dataset.max || 0), increment: Number(selected.dataset.increment || 1) } };
      activeEditor = this;
      replacing = true;
      messageKey = '';
      sync();
      let attempted = false;
      let navigating = false;
      try {
        const cartURL = new URL(this.dataset.cartUrl, location.origin);
        const productURL = new URL(this.dataset.productUrl, location.origin);
        if (cartURL.origin !== location.origin || productURL.origin !== location.origin) throw new Error('Invalid route');
        const cartPath = cartURL.pathname.replace(/\/$/, '');
        productURL.pathname = `${productURL.pathname.replace(/\/$/, '')}.js`;
        productURL.search = '';
        const [before, product] = await Promise.all([requestJSON(`${cartPath}.js`), requestJSON(productURL.href)]);
        const plan = replacementPlan(before, product, input);
        if (plan.error) {
          messageKey = plan.error;
          if (plan.error === 'stale') { uncertain = true; markReview(); }
          return;
        }
        if (!markReview()) { messageKey = 'ineligible'; return; }
        attempted = true;
        let added = false;
        try { await post(`${cartPath}/add.js`, { items: [plan.addItem] }); added = true; } catch (_) {}
        const afterAdd = await requestJSON(`${cartPath}.js`);
        const original = removableOriginal(afterAdd, plan);
        if (!added || !original) { uncertain = true; return; }
        let removed = false;
        try { await post(`${cartPath}/change.js`, { id: original.key, quantity: 0 }); removed = true; } catch (_) {}
        const after = await requestJSON(`${cartPath}.js`);
        if (!removed || !sameCart(after, plan.afterReplace)) { uncertain = true; return; }
        clearReview();
        location.assign(cartDestination(cartURL.href, 'updated'));
        navigating = true;
      } catch (_) {
        if (attempted) { uncertain = true; markReview(); }
        else messageKey = 'load';
      } finally {
        if (!navigating) replacing = false;
        sync();
      }
    }
  }
  customElements.define('fs-live-cart-editor', FsLiveCartEditor);
  const reviewCurrentCart = async () => {
    if (!reviewRequired || mutationLocked()) return;
    const page = document.querySelector('cart-items[data-fs-live-cart-root]');
    const form = page?.querySelector('#cart');
    if (!form) return;
    replacing = true;
    sync();
    try {
      const url = new URL(form.getAttribute('action'), location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid route');
      const cart = await requestJSON(`${url.pathname.replace(/\/$/, '')}.js`);
      const displayed = [...page.querySelectorAll('[data-fs-live-cart-key]')].map((row) => [row.dataset.fsLiveCartKey, Number(row.dataset.fsLiveCartQuantity)]).sort();
      const actual = cart.items.map((line) => [line.key, line.quantity]).sort();
      if (JSON.stringify(displayed) !== JSON.stringify(actual)) { uncertain = true; messageKey = 'stale'; return; }
      reviewRequired = false;
      messageKey = '';
      clearReview();
    } catch (_) { uncertain = true; } finally { replacing = false; sync(); }
  };
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('[data-fs-live-cart-reviewed]')) { event.preventDefault(); reviewCurrentCart(); return; }
    const checkout = target.closest('.cart__checkout-button, [name="checkout"], a[href*="/checkout"]');
    if (checkout && checkoutLocked()) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    const control = target.closest('[data-fs-live-cart-root] quantity-input button, [data-fs-live-cart-root] cart-remove-button, [data-fs-live-cart-apply]');
    if (control && (mutationLocked() || (reviewRequired && control.matches('[data-fs-live-cart-apply]')))) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (control?.matches('cart-remove-button')) queueNative();
  }, true);
  document.addEventListener('change', (event) => {
    if (!(event.target instanceof Element) || !event.target.matches('[data-fs-live-cart-root] .quantity__input')) return;
    if (mutationLocked()) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    queueNative();
  }, true);
  document.addEventListener('submit', (event) => {
    if (event.target.matches('#cart, #CartDrawer-Form') && checkoutLocked()) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  const start = () => {
    const url = new URL(location.href);
    const result = url.searchParams.get('fs_cart_result');
    if (result === 'review') { reviewRequired = true; markReview(); }
    else if (readMarker()) uncertain = true;
    else if (result === 'updated') messageKey = 'success';
    if (result) { url.searchParams.delete('fs_cart_result'); history.replaceState(history.state, '', url.href); }
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class'] });
    sync();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('pageshow', (event) => { if (event.persisted) { replacing = false; queued = false; uncertain = true; markReview(); sync(); } });
})();
